const WORDS_PER_MINUTE = 200;
const DEFAULT_EXCERPT_MAX_LENGTH = 140;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

/** Flatten Portable Text blocks into plain text for excerpts and reading-time estimates. */
export function extractPortableTextPlainText(content: unknown): string {
  if (!Array.isArray(content)) return "";

  const parts: string[] = [];
  for (const block of content) {
    if (!isRecord(block)) continue;

    if (block._type === "block" && Array.isArray(block.children)) {
      for (const child of block.children) {
        if (isRecord(child) && typeof child.text === "string" && child.text.trim()) {
          parts.push(child.text.trim());
        }
      }
      continue;
    }

    if (block._type === "image" && typeof block.alt === "string" && block.alt.trim()) {
      parts.push(block.alt.trim());
    }
  }

  return parts.join(" ").replace(/\s+/g, " ").trim();
}

/** First image URL embedded in lesson Portable Text content. */
export function extractPortableTextImageUrl(content: unknown): string | undefined {
  if (!Array.isArray(content)) return undefined;

  for (const block of content) {
    if (!isRecord(block) || block._type !== "image") continue;
    const asset = block.asset;
    if (!isRecord(asset)) continue;
    const url = typeof asset.url === "string" ? asset.url.trim() : "";
    if (url) return url;
  }

  return undefined;
}

export function estimateReadingTimeMinutesFromContent(
  content: unknown,
  fallbackMinutes?: number,
): number {
  const text = extractPortableTextPlainText(content);
  const words = text ? text.split(/\s+/).filter(Boolean).length : 0;
  if (words > 0) {
    return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE));
  }
  if (typeof fallbackMinutes === "number" && Number.isFinite(fallbackMinutes) && fallbackMinutes > 0) {
    return Math.ceil(fallbackMinutes);
  }
  return 1;
}

export function formatReadingTimeLabel(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 1) return "Less than 1 min read";
  if (minutes === 1) return "1 min read";
  return `${minutes} min read`;
}

export function buildLessonDescriptionExcerpt(
  plainText: string,
  maxLength: number = DEFAULT_EXCERPT_MAX_LENGTH,
): string {
  const normalized = plainText.replace(/\s+/g, " ").trim();
  if (!normalized) return "Open this lesson to start learning.";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trimEnd()}…`;
}

export type UniversityLessonCardModel = {
  title: string;
  description: string;
  imageSrc?: string;
  readingTimeLabel: string;
};

export function buildUniversityLessonCardModel(
  lesson: {
    title: string;
    content?: unknown;
    estimatedDuration?: number;
  },
  options?: {
    fallbackImageSrc?: string;
    excerptMaxLength?: number;
  },
): UniversityLessonCardModel {
  const plainText = extractPortableTextPlainText(lesson.content);
  const readingMinutes = estimateReadingTimeMinutesFromContent(
    lesson.content,
    lesson.estimatedDuration,
  );

  return {
    title: lesson.title,
    description: buildLessonDescriptionExcerpt(plainText, options?.excerptMaxLength),
    imageSrc: extractPortableTextImageUrl(lesson.content) ?? options?.fallbackImageSrc,
    readingTimeLabel: formatReadingTimeLabel(readingMinutes),
  };
}
